/**
 * PasswordPolicyService — Enforce password complexity, history, and expiry
 *
 * Covers TODO Phase 13.2 — Password Policy:
 *  - Minimum 10 characters
 *  - 1 uppercase, 1 lowercase, 1 number, 1 special character
 *  - No reuse of last 5 passwords
 *  - Forced change every 90 days (configurable)
 */

import CryptoJS from 'crypto-js';

const DEFAULT_CONFIG = {
  minLength: 10,
  requireUppercase: true,
  requireLowercase: true,
  requireNumber: true,
  requireSpecial: true,
  maxAgeDays: 90,
  historyCount: 5,
  commonPasswordsCheck: true,
};

const COMMON_PASSWORDS = new Set([
  'password123',
  'qwerty12345',
  'admin12345',
  'letmein1234',
  'welcome1234',
  'changeme123',
  'p@ssword123',
  'abc12345678',
]);

class PasswordPolicyService {
  constructor(config = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  validate(password) {
    const errors = [];
    const c = this.config;

    if (!password || password.length < c.minLength) {
      errors.push(`Must be at least ${c.minLength} characters`);
    }
    if (c.requireUppercase && !/[A-Z]/.test(password)) {
      errors.push('Must contain at least 1 uppercase letter');
    }
    if (c.requireLowercase && !/[a-z]/.test(password)) {
      errors.push('Must contain at least 1 lowercase letter');
    }
    if (c.requireNumber && !/\d/.test(password)) {
      errors.push('Must contain at least 1 number');
    }
    if (c.requireSpecial && !/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password)) {
      errors.push('Must contain at least 1 special character');
    }
    if (c.commonPasswordsCheck && COMMON_PASSWORDS.has(password?.toLowerCase())) {
      errors.push('This password is too common');
    }

    return {
      valid: errors.length === 0,
      errors,
      strength: this._calculateStrength(password),
    };
  }

  checkReuse(newPassword, passwordHistory = []) {
    const newHash = CryptoJS.SHA256(newPassword).toString();
    const recentHashes = passwordHistory.slice(0, this.config.historyCount);

    for (const oldHash of recentHashes) {
      if (oldHash === newHash) {
        return {
          allowed: false,
          error: `Cannot reuse any of the last ${this.config.historyCount} passwords`,
        };
      }
    }
    return { allowed: true };
  }

  isExpired(passwordChangedAt) {
    if (!passwordChangedAt) return true;
    const changed = new Date(passwordChangedAt);
    const now = new Date();
    const daysSinceChange = (now - changed) / (1000 * 60 * 60 * 24);
    return daysSinceChange > this.config.maxAgeDays;
  }

  getDaysUntilExpiry(passwordChangedAt) {
    if (!passwordChangedAt) return 0;
    const changed = new Date(passwordChangedAt);
    const expiryDate = new Date(changed);
    expiryDate.setDate(expiryDate.getDate() + this.config.maxAgeDays);
    const remaining = (expiryDate - new Date()) / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.ceil(remaining));
  }

  _calculateStrength(password) {
    if (!password) return { score: 0, label: 'None' };
    let score = 0;

    if (password.length >= 10) score += 1;
    if (password.length >= 14) score += 1;
    if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score += 1;
    if (/\d/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;
    if (password.length >= 18) score += 1;

    const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong', 'Excellent'];
    return { score, label: labels[Math.min(score, labels.length - 1)] };
  }

  getPolicy() {
    return { ...this.config };
  }
}

const instance = new PasswordPolicyService();
export default instance;
